import React from 'react';
import { BiChevronDown, BiMenu, BiX } from 'react-icons/bi';
import { IMenuItem } from './IMenuItem.ts';
import { Logo } from './Logo';
import { IProps } from './types';
import './UIMenu.scss';

const SWIPE_THRESHOLD_PX = 20;
const SWIPE_TIMEOUT_MS = 500;

interface TouchStart {
  x: number;
  y: number;
  time: number;
}

export const UIMenu = (props: IProps): React.ReactElement => {
  const [showMobileMenu, setShowMobileMenu] = React.useState<boolean>(false);
  const [expandedSubmenus, setExpandedSubmenus] = React.useState<Set<string>>(new Set());
  // Desktop submenus open on CSS :hover alone. Since client-side navigation
  // swaps the page without moving the mouse, the cursor can stay parked over
  // the trigger and the dropdown would otherwise stay open after a click.
  // This tracks submenus that should ignore :hover until the mouse actually
  // leaves them.
  const [dismissedSubmenus, setDismissedSubmenus] = React.useState<Set<string>>(new Set());

  const touchStartRef = React.useRef<TouchStart | null>(null);

  const getSubmenuKey = (menuItem: IMenuItem): string => {
    return menuItem.link ?? menuItem.title ?? '';
  };

  const toggleSubmenu = (key: string): void => {
    setExpandedSubmenus((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const dismissSubmenuHover = (key: string): void => {
    setDismissedSubmenus((prev) => new Set(prev).add(key));
  };

  const restoreSubmenuHover = (key: string): void => {
    setDismissedSubmenus((prev) => {
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // Track page scroll to toggle the compact/active header state, throttled
  // so it isn't recomputed on every scroll event.
  // Close the mobile menu on Escape.
  React.useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('keyup', handleKeyUp);

    return (): void => document.removeEventListener('keyup', handleKeyUp);
  }, []);

  // Close the mobile menu on a left swipe.
  React.useEffect(() => {
    const handleTouchStart = (event: TouchEvent): void => {
      const touch = event.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    };

    const handleTouchEnd = (event: TouchEvent): void => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start) {
        return;
      }

      const touch = event.changedTouches[0];
      const xDiff = start.x - touch.clientX;
      const yDiff = start.y - touch.clientY;
      const timeDiff = Date.now() - start.time;

      const isSwipeLeft = Math.abs(xDiff) > Math.abs(yDiff)
        && xDiff > SWIPE_THRESHOLD_PX
        && timeDiff < SWIPE_TIMEOUT_MS;

      if (isSwipeLeft) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return (): void => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Lock body scroll while the mobile menu is open. `document.body` lives
  // outside this component's own render tree, so it can't be driven by
  // className alone and needs an effect.
  React.useEffect(() => {
    document.body.classList.toggle('nav--disable-scroll', showMobileMenu);

    return (): void => {
      document.body.classList.remove('nav--disable-scroll');
    };
  }, [showMobileMenu]);

  const getMenuItem = (menuItem: IMenuItem): React.ReactNode => {
    if (menuItem.menuItems && menuItem.menuItems.length > 0) {
      const submenuKey = getSubmenuKey(menuItem);
      const isExpanded = expandedSubmenus.has(submenuKey);

      return (
        <>
          <button
            type='button'
            className='nav-sub-btn'
            aria-expanded={ showMobileMenu ? isExpanded : undefined }
            onClick={ () => {
              if (showMobileMenu) {
                toggleSubmenu(submenuKey);
              }
            } }>
            {menuItem.title}
            {showMobileMenu && (
              <BiChevronDown />
            )}
          </button>
          <div className={ `nav-sub-content${isExpanded ? ' nav-sub-content--expanded' : ''}` }>
            {menuItem.menuItems?.map((tempMenuItem: IMenuItem) => {
              return (
                <a
                  key={ tempMenuItem.link }
                  href={ tempMenuItem.link }
                  className={ (tempMenuItem.active ? ' active' : '') }
                  onClick={ (event) => {
                    dismissSubmenuHover(submenuKey);
                    handleClick(event, tempMenuItem);
                  } }>
                  {tempMenuItem.title}
                </a>
              );
            })}
          </div>
        </>
      );
    } else if (menuItem.button) {
      return (
        <a
          onClick={ (event) => {
            handleClick(event, menuItem);
          } }>
          {menuItem.title}
        </a>
      );
    } else {
      return (
        <a
          href={ menuItem.link }
          onClick={ (event) => {
            handleClick(event, menuItem);
          } }>
          {menuItem.title}
        </a>
      );
    }
  };

  const handleClick = (event: React.MouseEvent, menuItem: IMenuItem): void => {
    event.preventDefault();
    setShowMobileMenu(false);

    props.onNavigate(menuItem);
  };

  const hasActiveChildren = (menuItem: IMenuItem): boolean => {
    return menuItem.menuItems?.some((tempMenuItem) => tempMenuItem.active) ?? false;
  };

  return (
    <div
      className={ `ui-navigation nav-wrapper--active${showMobileMenu ? ' sidebar-visible' : ''}` }>
      <div className='nav-container'>
        <button
          type='button'
          data-test-id='nav-hamburger'
          className='nav-hamburger'
          aria-label='Open menu'
          aria-expanded={ showMobileMenu }
          onClick={ () => {
            setShowMobileMenu(true);
          } }>
          <BiMenu style={ {
            fill: 'black'
          } } />
        </button>

        <Logo
          isScrolling={ true }
          onClick={ handleClick } />

        <div
          className={ `nav-background${showMobileMenu ? ' nav-background-active' : ''}` }
          onClick={ () => {
            if (showMobileMenu) {
              setShowMobileMenu(false);
            }
          } } />

        <nav className={ `nav${showMobileMenu ? ' nav-active' : ''}` }>
          <div className='nav-responsive-header'>
            <Logo
              isMobile={ true }
              isScrolling={ true }
              onClick={ handleClick } />

            <button
              type='button'
              className='nav-hamburger'
              aria-label='Close menu'
              onClick={ () => {
                setShowMobileMenu(false);
              } }>
              <BiX style={ { fill: 'black' } } />
            </button>
          </div>
          <div className='nav-content'>
            {props.menuItems.map((menuItem: IMenuItem) => {
              if (menuItem.icon) {
                return (
                  <a
                    className={ 'menu-icon' }
                    key={ menuItem.link }
                    target={ '_blank' }
                    href={ menuItem.link }
                    aria-label={ menuItem.title }
                    rel='noreferrer'>
                    {menuItem.icon}
                  </a>
                );
              }

              const hasSubmenu = !!(menuItem.menuItems && menuItem.menuItems.length > 0);
              const submenuKey = getSubmenuKey(menuItem);

              return (
                <div
                  data-test-id={ menuItem.dataTestId }
                  className={ `nav-item ${hasSubmenu ? 'nav-sub' : ''}${menuItem.active || hasActiveChildren(menuItem) ? ' active' : ''}${menuItem.button ? ' nav-item--primary' : ''}${hasSubmenu && dismissedSubmenus.has(submenuKey) ? ' nav-sub--dismissed' : ''}` }
                  onMouseLeave={ hasSubmenu ? (): void => {
                    restoreSubmenuHover(submenuKey);
                  } : undefined }
                  key={ menuItem.link }>
                  {getMenuItem(menuItem)}
                </div>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
};
